import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, Marker, Polygon, useLoadScript } from '@react-google-maps/api';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack,
  ToggleButton, ToggleButtonGroup, Typography, useTheme,
} from '@mui/material';
import HighlightAltIcon from '@mui/icons-material/HighlightAlt';
import { useMapPolygonDrawing } from '../../hooks/useMapPolygonDrawing';
import { mapSelectableDevices } from '../../utils/mapSelectUtils';
import { normalizeSerial } from '../../utils/optimizeScopeUtils';

// Same loader options as every other map in the app (the loader refuses a
// second load with different options).
const GOOGLE_MAPS_API_KEY = 'AIzaSyDBzNIMvmobitJ-vi8Z-kzVfK99qSb7jRw';
const GOOGLE_MAPS_LIBRARIES = ['geometry', 'marker'];
const MAP_STYLE = { width: '100%', height: '100%' };

/**
 * Pick devices on a map: draw an area (point by point, close it on the
 * first point, as with the main map's Select Devices) to add or remove
 * every device inside, or click a device to toggle it.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {string} props.title
 * @param {Array} props.devices site devices (those without a position are not shown)
 * @param {string[]} [props.initialSerials] selection to start from
 * @param {string} [props.hint] what the selection is used for
 * @param {(selected:string[]) => React.ReactNode} [props.renderOptions] extra controls and a preview, left of the buttons
 * @param {string} [props.applyLabel]
 * @param {() => void} props.onClose
 * @param {(selected:string[]) => void} props.onApply selected serials, in device-list order
 */
export default function MapSelectDialog({
  open, title, devices, initialSerials, hint, renderOptions, applyLabel = 'Use selection', onClose, onApply,
}) {
  const theme = useTheme();
  const mapRef = useRef(null);
  const { isLoaded, loadError } = useLoadScript({ googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries: GOOGLE_MAPS_LIBRARIES });
  const points = useMemo(() => mapSelectableDevices(devices), [devices]);
  const [selected, setSelected] = useState(() => new Set());
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const fitRef = useRef(() => {});
  const [areaMode, setAreaMode] = useState('add');

  // Start from initialSerials each time the dialog opens, and only then:
  // callers pass a fresh array on every render.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      const wanted = new Set((initialSerials || []).map(normalizeSerial));
      const initial = new Set(points.filter((p) => wanted.has(normalizeSerial(p.serial))).map((p) => p.serial));
      selectedRef.current = initial;
      setSelected(initial);
      setAreaMode('add');
      // The map can load before this runs (its effects run first).
      fitRef.current();
    }
    wasOpen.current = open;
  }, [open, initialSerials, points]);

  const onAreaDrawn = useCallback((inside) => {
    setSelected((prev) => {
      const next = new Set(prev);
      inside.forEach((p) => (areaMode === 'add' ? next.add(p.serial) : next.delete(p.serial)));
      return next;
    });
  }, [areaMode]);
  const {
    toggleDrawingMode, polygons, clearPolygons, isDrawing,
  } = useMapPolygonDrawing({ mapRef, devicesWithLocation: points, onSelectionComplete: onAreaDrawn });

  const toggleDevice = (serial) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(serial)) next.delete(serial);
    else next.add(serial);
    return next;
  });
  useEffect(() => {
    if (!open) clearPolygons();
  }, [open, clearPolygons]);
  const clearAll = () => {
    setSelected(new Set());
    clearPolygons();
  };

  // Frame the selection (or every device) when the map opens.
  const fit = useCallback(() => {
    const map = mapRef.current;
    if (!map || !window.google?.maps || points.length === 0) return;
    const chosen = points.filter((p) => selectedRef.current.has(p.serial));
    const bounds = new window.google.maps.LatLngBounds();
    (chosen.length ? chosen : points).forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    map.fitBounds(bounds, 60);
  }, [points]);
  fitRef.current = fit;
  // Stable, so re-renders (every click) do not re-center the map: the
  // map applies `center` whenever the object changes.
  const initialCenter = useMemo(
    () => (points[0] ? { lat: points[0].lat, lng: points[0].lng } : { lat: 0, lng: 0 }),
    [points],
  );

  const ordered = points.filter((p) => selected.has(p.serial)).map((p) => p.serial);
  const capCount = points.filter((p) => p.isCap && selected.has(p.serial)).length;
  const hidden = (devices || []).length - points.length;
  const colorScheme = isLoaded && window.google?.maps?.ColorScheme
    ? window.google.maps.ColorScheme[theme.palette.mode === 'dark' ? 'DARK' : 'LIGHT']
    : undefined;

  const renderMap = () => {
    if (loadError) return <Alert severity="error">Failed to load Google Maps.</Alert>;
    if (!isLoaded) {
      return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><CircularProgress /></Box>;
    }
    const { SymbolPath } = window.google.maps;
    return (
      <GoogleMap
        mapContainerStyle={MAP_STYLE}
        center={initialCenter}
        zoom={15}
        onLoad={(map) => {
          mapRef.current = map;
          fit();
        }}
        onUnmount={() => { mapRef.current = null; }}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          gestureHandling: 'greedy',
          draggableCursor: isDrawing ? 'crosshair' : undefined,
          ...(colorScheme ? { colorScheme } : {}),
        }}
      >
        {polygons.map((pg) => (
          <Polygon
            key={pg.id}
            paths={pg.coordinates}
            options={{
              strokeColor: theme.palette.primary.main,
              strokeWeight: 2,
              fillColor: theme.palette.primary.main,
              fillOpacity: 0.08,
              clickable: false,
            }}
          />
        ))}
        {points.map((p) => {
          const on = selected.has(p.serial);
          return (
            <Marker
              key={p.serial}
              position={{ lat: p.lat, lng: p.lng }}
              title={`${p.name ? `${p.name} · ` : ''}${p.serial}${p.isCap ? ' (CAP)' : ''}`}
              // While drawing, clicks must reach the map to place points.
              clickable={!isDrawing}
              onClick={() => toggleDevice(p.serial)}
              zIndex={p.isCap ? 3 : on ? 2 : 1}
              icon={{
                path: SymbolPath.CIRCLE,
                scale: p.isCap ? 8 : 5.5,
                fillColor: on ? theme.palette.primary.main : theme.palette.grey[500],
                fillOpacity: on ? 1 : 0.55,
                strokeColor: p.isCap ? theme.palette.common.black : theme.palette.common.white,
                strokeWeight: p.isCap ? 2.5 : 1.5,
              }}
            />
          );
        })}
      </GoogleMap>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ position: 'relative', height: '62vh', minHeight: 360 }}>
          {renderMap()}
          {isLoaded && (
            <Paper
              elevation={3}
              sx={{ position: 'absolute', top: 12, left: 12, p: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', maxWidth: 'calc(100% - 24px)' }}
            >
              <Button
                size="small"
                variant={isDrawing ? 'contained' : 'outlined'}
                startIcon={<HighlightAltIcon />}
                onClick={toggleDrawingMode}
              >
                {isDrawing ? 'Cancel' : 'Draw area'}
              </Button>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={areaMode}
                onChange={(_e, v) => v && setAreaMode(v)}
                disabled={isDrawing}
              >
                <ToggleButton value="add" sx={{ textTransform: 'none', py: 0.25 }}>Add</ToggleButton>
                <ToggleButton value="remove" sx={{ textTransform: 'none', py: 0.25 }}>Remove</ToggleButton>
              </ToggleButtonGroup>
              <Button size="small" onClick={clearAll} disabled={isDrawing || (selected.size === 0 && polygons.length === 0)}>
                Clear
              </Button>
              <Typography variant="body2" sx={{ px: 0.5 }}>
                <strong>{selected.size}</strong> selected{capCount ? ` (${capCount} CAP${capCount === 1 ? '' : 's'})` : ''}
              </Typography>
            </Paper>
          )}
          {isDrawing && (
            <Paper elevation={3} sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', px: 1.5, py: 0.75 }}>
              <Typography variant="caption">
                Click to add points; click the first point to close the area. Devices inside are
                {areaMode === 'add' ? ' added to' : ' removed from'} the selection.
              </Typography>
            </Paper>
          )}
        </Box>
        <Stack spacing={0.5} sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {hint ? `${hint} ` : ''}Click a device to add or remove it. CAPs have a dark outline.
          </Typography>
          {hidden > 0 && (
            <Typography variant="caption" color="text.secondary">
              {hidden} device{hidden === 1 ? ' has' : 's have'} no position and {hidden === 1 ? 'is' : 'are'} not on the map.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, pl: 1 }}>{renderOptions ? renderOptions(ordered) : null}</Box>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={ordered.length === 0 || isDrawing} onClick={() => onApply(ordered)}>
          {applyLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
